import React, { PropsWithChildren } from 'react'
import SessionRepository from 'domain/repository/session/SessionRepository'
import tokenRefreshService from 'grpc/interceptors/tokenRefreshInstance'
import OAuthRepository from 'domain/repository/oauth/OAuthRepository'
import SessionUseCase from './domain/usecase/session/SessionService'
import { LoadingSpinner } from './presentation/shared/LoadingSpinner'

export interface User {
	uuid: string
	email: string
	role: string
}

interface AppContextData {
	authorized: boolean
	loading: boolean
	user: User | null
	signIn(): Promise<void>
	signOut(): Promise<void>
}

const AppContext = React.createContext<AppContextData>({} as AppContextData)

const AppProvider = ({ children }: PropsWithChildren) => {
	const sessionUseCase = new SessionUseCase(new SessionRepository())

	const [loading, setLoading] = React.useState(true)
	const [user, setUser] = React.useState<User | null>(null)

	React.useEffect(() => {
		tokenRefreshService.onSessionExpired(() => {
			setUser(null)
		})
		signIn()
	}, [])

	async function signIn() {
		try {
			const sessionRepo = new SessionRepository()
			const accessToken = sessionRepo.getAccessToken()

			if (accessToken == null) {
				setUser(null)
				return
			}

			// Если токен истёк и refresh тоже нет — очищаем сессию
			if (!accessToken.isValidToken() && !sessionRepo.getRefreshTokenRaw()) {
				sessionRepo.destroyToken()
				setUser(null)
				return
			}

			// Читаем claims из JWT без сетевого запроса.
			// Если токен истёк, но refresh token есть — пользователь остаётся залогиненным,
			// а реальный refresh произойдёт лениво при первом gRPC-запросе.
			setUser({
				uuid: accessToken.getUid() ?? '',
				email: accessToken.getEmail() ?? '',
				role: accessToken.getRole() ?? '',
			})
		} catch (error) {
			console.error('Failed to sign in:', error)
			setUser(null)
		} finally {
			setLoading(false)
		}
	}

	async function signOut() {
		const sessionRepo = new SessionRepository()
		const accessTokenRaw = sessionRepo.getAccessTokenRaw()
		if (accessTokenRaw) {
			try {
				await new OAuthRepository().signOut(accessTokenRaw)
			} catch (error) {
				console.error('OAuth logout error:', error)
			}
		}
		OAuthRepository.clearProvider()
		setUser(null)
		sessionUseCase.destroyToken()
	}

	if (loading) {
		return (
			<div className='fixed inset-0 flex items-center justify-center'>
				<LoadingSpinner />
			</div>
		)
	}

	return (
		<AppContext.Provider
			value={{
				authorized: !!user,
				user,
				loading,
				signIn,
				signOut,
			}}>
			{children}
		</AppContext.Provider>
	)
}

export { AppContext, AppProvider }
